import {
  Survey,
  SurveyMetadataFormState,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
  surveyToMarginForm,
  SurveyMarginFormState,
  surveyToMetadataForm,
} from '@ps/survey-utils';

import { SuppliersMetaData } from '../survey-creation-suppliers-meta-data-state.model';
import { DEFAULT_CHILD_AUDIENCE_STATE } from '../../survey-creation-state.constants';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from '../../survey-creation-state.model';

export function buildMetadataFormStateFromSurvey(
  survey: Survey,
): SurveyMetadataFormState {
  return {
    value: surveyToMetadataForm(survey),
    valid: true,
    errors: [],
    isDirty: false,
  };
}

export function buildMarginFormStateFromSurvey(
  survey: Survey,
): SurveyMarginFormState {
  return {
    value: surveyToMarginForm(survey),
    valid: true,
    errors: [],
    isDirty: false,
  };
}

export function buildQualificationsFormStateFromSurvey(
  survey: Survey,
): SurveyQualificationsFormState {
  return {
    value: survey.qualifications ?? [],
    valid: true,
    errors: [],
    isDirty: false,
  };
}

export function buildQuotasFormStateFromSurvey(
  survey: Survey,
): SurveyQuotasFormState {
  return {
    value: survey.quotas ?? [],
    valid: true,
    errors: [],
    isDirty: false,
  };
}

// surveyPauseThreshold / manualOverride are not on `Survey` — they arrive via
// the flexibility-prices load and the apply-mirror effect, so they seed null/false
// here and are filled once those land.
export function buildSuppliersMetaDataFromSurvey(
  survey: Survey,
): SuppliersMetaData {
  return {
    blendId: survey.blendId,
    priceOverride: survey.cpi?.priceOverride ?? null,
    defaultPurePriceOverride: !!survey.defaultPurePriceOverride,
    isPurePriceRateCardEnable: survey.isPurePriceRateCardEnable,
    supplierDefaultAllocation: survey.supplierDefaultAllocation,
    surveyPauseThreshold: null,
    manualOverride: false,
  };
}

export type HydratedChildAudienceState = Pick<
  SurveyCreationChildAudienceState,
  | 'advancedFeasibilityFilters'
  | 'metadata'
  | 'margin'
  | 'qualifications'
  | 'quotas'
  | 'suppliersMetaData'
>;

export function hydrateChildFromSurvey(
  survey: Survey,
): HydratedChildAudienceState {
  return {
    advancedFeasibilityFilters: survey.advancedFeasibilityFilters,
    metadata: buildMetadataFormStateFromSurvey(survey),
    margin: buildMarginFormStateFromSurvey(survey),
    qualifications: buildQualificationsFormStateFromSurvey(survey),
    quotas: buildQuotasFormStateFromSurvey(survey),
    suppliersMetaData: buildSuppliersMetaDataFromSurvey(survey),
  };
}

export function buildLoadedChild(
  survey: Survey,
): SurveyCreationChildAudienceState {
  return {
    ...DEFAULT_CHILD_AUDIENCE_STATE,
    ...hydrateChildFromSurvey(survey),
  };
}

export function buildLoadedRegistryChild(
  survey: Survey,
): SurveyChildRegistryState {
  return { survey, error: null, participant: true };
}
